import { DotsThreeVertical } from "@phosphor-icons/react";
import { useState } from "react";

import CopiableTag from "common/components/CopiableTag";
import { Button } from "common/components/ui/Button";
import { Dialog, DialogContent } from "common/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuList,
  DropdownMenuTrigger,
} from "common/components/ui/DropdownMenu";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import { typedObjectEntries } from "common/helpers/utils";

import { API_KEYS_ACTIONS } from "../../../constants";
import { formatBillingDate } from "../../../utils";
import ApiKeyActions from "../ApiKeyActions";

import type { ApiToken } from "../../../types";

interface DesktopTableProps {
  tokens: ApiToken[];
}

export default function DesktopTable({ tokens }: DesktopTableProps) {
  const [actionState, setActionState] = useState<{
    isOpen: boolean;
    action: "remove" | "edit";
    selectedToken: ApiToken;
  }>({ isOpen: false, action: "edit", selectedToken: undefined });

  const { isOpen, action, selectedToken } = actionState;

  return (
    <>
      <Table>
        <thead>
          <tr>
            <TableHead className="w-64">Key name</TableHead>
            <TableHead className="w-32">Permissions</TableHead>
            <TableHead className="w-52">Key</TableHead>
            <TableHead>Created at</TableHead>
            <TableHead />
          </tr>
        </thead>

        {/* Table body */}
        <tbody>
          {tokens.map((token) => {
            const {
              id,
              name,
              token: tokenId,
              created_at: createdAt,
              write: canWrite,
              read: canRead,
            } = token;

            const permissions = canWrite && canRead ? "All" : "Read only";

            return (
              <TableRow key={id}>
                {/* Token name */}
                <TableCell className="text-sm-bold">{name}</TableCell>

                {/* Token permission */}
                <TableCell>{permissions}</TableCell>

                {/* Token ID / Key */}
                <TableCell>
                  <CopiableTag value={tokenId} />
                </TableCell>

                {/* Token created at date */}
                <TableCell>{formatBillingDate(createdAt)}</TableCell>

                {/* Actions buttons for editing & deleting tokens */}
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        intent="iconOnly"
                        variant="secondary-black"
                        size="sm"
                      >
                        <DotsThreeVertical />
                      </Button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" size="sm">
                      <DropdownMenuList>
                        {typedObjectEntries(API_KEYS_ACTIONS).map(
                          ([key, { icon: Icon, label, ...rest }]) => (
                            <DropdownMenuItem
                              key={key}
                              onClick={() => {
                                setActionState({
                                  isOpen: true,
                                  action: key,
                                  selectedToken: token,
                                });
                              }}
                              variant={
                                "isDanger" in rest ? "danger" : "default"
                              }
                            >
                              <Icon /> {label}
                            </DropdownMenuItem>
                          ),
                        )}
                      </DropdownMenuList>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </tbody>
      </Table>
      {selectedToken && (
        <Dialog
          open={isOpen}
          onOpenChange={(isOpen) => setActionState({ ...actionState, isOpen })}
        >
          <DialogContent size="md">
            <ApiKeyActions
              token={selectedToken}
              action={action}
              onClose={() => setActionState({ ...actionState, isOpen: false })}
              onBack={() => setActionState({ ...actionState, isOpen: false })}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
